import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { IconButton } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

const BAR_COUNT = 40;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 220,
    maxWidth: 330,
    padding: "2px 4px",
    marginRight: 22,
  },
  playButton: {
    padding: 6,
    color: theme.palette.mode === "light" ? "#54656f" : "#aebac1",
  },
  waveWrapper: {
    position: "relative",
    flex: 1,
    height: 28,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  bars: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    width: "100%",
    height: "100%",
  },
  bar: {
    flex: 1,
    borderRadius: 2,
    minWidth: 2,
    transition: "background-color 0.1s linear",
  },
  time: {
    fontSize: 11,
    color: theme.palette.mode === "light" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.6)",
    minWidth: 32,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  rateButton: {
    fontSize: 11,
    fontWeight: 600,
    minWidth: 26,
    textAlign: "center",
    cursor: "pointer",
    userSelect: "none",
    color: theme.palette.mode === "light" ? "#54656f" : "#aebac1",
  },
}));

const RATE_STORAGE_KEY = "audioMessageRate";
const RATE_STEPS = [0.5, 1, 1.5, 2, 4];

const nextRate = (rate) => RATE_STEPS[(RATE_STEPS.indexOf(rate) + 1) % RATE_STEPS.length];

const formatTime = (secs) => {
  if (!secs || Number.isNaN(secs) || !Number.isFinite(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

const buildHeights = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const heights = [];
  for (let i = 0; i < BAR_COUNT; i += 1) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    heights.push(0.25 + (h % 1000) / 1000 * 0.75);
  }
  return heights;
};

const AudioPlayer = ({ src, fromMe }) => {
  const { classes, theme } = useStyles();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(() => parseFloat(localStorage.getItem(RATE_STORAGE_KEY) || "1"));

  const heights = useMemo(() => buildHeights(src || "audio"), [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onLoaded = () => setDuration(audio.duration);
    const onTime = () => setCurrent(audio.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const toggleRate = () => {
    const updated = nextRate(rate);
    setRate(updated);
    localStorage.setItem(RATE_STORAGE_KEY, String(updated));
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrent(audio.currentTime);
  };

  const progress = duration ? current / duration : 0;
  const playedColor = fromMe ? "#4fa394" : theme.palette.primary.main;
  const idleColor = theme.palette.mode === "light" ? "#c8d0d4" : "#556067";

  return (
    <div className={classes.root}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <IconButton className={classes.playButton} onClick={togglePlay} size="small">
        {playing ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      <div className={classes.waveWrapper} onClick={seek}>
        <div className={classes.bars}>
          {heights.map((hRatio, i) => (
            <span
              key={i}
              className={classes.bar}
              style={{
                height: `${Math.round(hRatio * 100)}%`,
                backgroundColor: i / BAR_COUNT <= progress ? playedColor : idleColor,
              }}
            />
          ))}
        </div>
      </div>

      <span className={classes.time}>
        {formatTime(playing || current ? current : duration)}
      </span>

      <span className={classes.rateButton} onClick={toggleRate}>
        {rate}x
      </span>
    </div>
  );
};

export default AudioPlayer;
